/**
 * The continuous uniform distribution U(a, b), in the form R's `dunif`, `punif` and
 * `qunif` use, and the discrete uniform on the integers a, a + 1, …, b (a fair die is
 * a = 1, b = 6; SciPy's `randint(a, b + 1)`).
 *
 * The discrete one is expressed as a `DiscreteModel` so the shared six-question
 * summary, table and bar chart of the binomial and Poisson calculators work on it
 * unchanged.
 */

import type { DiscreteModel } from "@/lib/tool-kit/stats-distributions/discrete";

export function uniformPdf(x: number, a: number, b: number): number {
  return x >= a && x <= b ? 1 / (b - a) : 0;
}

/** P(X ≤ x). */
export function uniformCdf(x: number, a: number, b: number): number {
  if (x <= a) {
    return 0;
  }
  if (x >= b) {
    return 1;
  }
  return (x - a) / (b - a);
}

/** P(X > x). */
export function uniformSurvival(x: number, a: number, b: number): number {
  if (x <= a) {
    return 1;
  }
  if (x >= b) {
    return 0;
  }
  return (b - x) / (b - a);
}

/** P(x₁ ≤ X ≤ x₂), the two bounds put in order: the overlap with [a, b] over its length. */
export function uniformBetween(
  x1: number,
  x2: number,
  a: number,
  b: number
): number {
  const low = Math.max(a, Math.min(x1, x2));
  const high = Math.min(b, Math.max(x1, x2));
  return high > low ? (high - low) / (b - a) : 0;
}

/** The x with P(X ≤ x) = p, for 0 ≤ p ≤ 1. */
export function uniformQuantile(p: number, a: number, b: number): number {
  return a + p * (b - a);
}

export function uniformMoments(a: number, b: number) {
  const variance = (b - a) ** 2 / 12;
  return {
    mean: (a + b) / 2,
    variance,
    sd: Math.sqrt(variance),
    median: (a + b) / 2,
    range: b - a,
  };
}

/** The discrete uniform on the integers a..b (a ≤ b), as a `DiscreteModel`. */
export function discreteUniformModel(a: number, b: number): DiscreteModel {
  const n = b - a + 1;
  return {
    pmf: (k) => (Number.isInteger(k) && k >= a && k <= b ? 1 / n : 0),
    cdf: (k) => {
      if (k < a) {
        return 0;
      }
      if (k >= b) {
        return 1;
      }
      return (Math.floor(k) - a + 1) / n;
    },
    upper: (k) => {
      if (k <= a) {
        return 1;
      }
      if (k > b) {
        return 0;
      }
      return (b - Math.ceil(k) + 1) / n;
    },
    mean: (a + b) / 2,
    variance: (n * n - 1) / 12,
    support: [a, b],
  };
}
